import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import _uniqueId from 'lodash/uniqueId';
import { AreaTagI } from '../../../components/ImageTagger';
import { InfringementDetectionFeatureTreeType, InfringementDetectionI, UpdatedScoresType } from '../types';

interface SearchState {
  id: string;
  searchName: string;
  productDescription: string;
  pdf: { pdfId: string; pdfName: string }[];
  word: { wordId: string; wordName: string }[];
  images: { imageId: string; imageUrl: string }[];
  imagesFromSearchHistory: { imageId: string; imageUrl: string }[];
  imageTags: { imageId: string; tags: { tagName: string; tagId: string }[] }[];
  imageAreaTags: { imageId: string; areas: AreaTagI[] }[];
  imageAreaTagsTemp: { imageId: string; areas: AreaTagI[] }[];
  featureTree: InfringementDetectionFeatureTreeType | null;
  groupByFamily: boolean;
  assignees: string[];
  countryCodes: string[];
  statuses: string[];
  priorityFromDate: string;
  priorityToDate: string;
  patentsWithFeedback: string[];
  updatedScores: UpdatedScoresType | null;
  isDuplicateExist: boolean;
  updatedAt: string;
  regenerateTree: boolean;
  featureTreeLoading: boolean;
  saveTreeChanges: boolean;
  selectPatentsMode: boolean;
  generateClaimsMode: boolean;
  patentsToGenerateClaims: string[];
  newPatentsToGenerateClaims: string[];
  subsystemAnalysisInfo: { subsystemAnalysisId: string; name: string }[];
  currentSubsystemId: string;
  selectedClaim: {
    claimId: string;
    patentId: string;
    sectionId: string;
    sectionPosition: number;
  } | null;
  isLoadingScore: boolean;
}

const initialState: SearchState = {
  id: '',
  searchName: '',
  productDescription: '',
  images: [],
  pdf: [],
  word: [],
  imagesFromSearchHistory: [],
  imageTags: [],
  imageAreaTags: [],
  imageAreaTagsTemp: [],
  featureTree: null,
  groupByFamily: false,
  assignees: [],
  countryCodes: [],
  statuses: [],
  priorityFromDate: '',
  priorityToDate: '',
  currentSubsystemId: '',
  patentsWithFeedback: [],
  updatedScores: null,
  isDuplicateExist: false,
  updatedAt: '',
  regenerateTree: false,
  featureTreeLoading: false,
  saveTreeChanges: false,
  generateClaimsMode: false,
  selectPatentsMode: false,
  patentsToGenerateClaims: [],
  newPatentsToGenerateClaims: [],
  subsystemAnalysisInfo: [],
  selectedClaim: null,
  isLoadingScore: false,
};

const infringementDetectionSlice = createSlice({
  name: 'infringementDetection',
  initialState,
  reducers: {
    setID_SearchName: (state, action: PayloadAction<string>) => {
      state.searchName = action.payload;
    },
    setID_Id: (state, action: PayloadAction<string>) => {
      state.id = action.payload;
    },
    setID_ProductDescription: (state, action: PayloadAction<string>) => {
      state.productDescription = action.payload;
    },
    setID_GroupByFamily: (state, action: PayloadAction<boolean>) => {
      state.groupByFamily = action.payload;
    },
    setID_PDF: (state, action: PayloadAction<{ pdfId: string; pdfName: string }>) => {
      if (!state.pdf.find(file => file.pdfId === action.payload.pdfId)) {
        state.pdf = [...state.pdf, action.payload];
      }
    },
    removeID_PDF: (state, action: PayloadAction<string>) => {
      state.pdf = state.pdf.filter(file => file.pdfId !== action.payload);
    },
    setID_WordFile: (state, action: PayloadAction<{ wordId: string; wordName: string }>) => {
      if (!state.word.find(file => file.wordId === action.payload.wordId)) {
        state.word = [...state.word, action.payload];
      }
    },
    removeID_WordFile: (state, action: PayloadAction<string>) => {
      state.word = state.word.filter(file => file.wordId !== action.payload);
    },
    setID_Images: (state, action: PayloadAction<{ imageId: string; imageUrl: string }>) => {
      if (!state.images.find(image => image.imageId === action.payload.imageId)) {
        state.images = [...state.images, action.payload];
      }
    },
    setID_ImagesFromSearchHistory: (state, action: PayloadAction<{ imageId: string; imageUrl: string }[]>) => {
      state.imagesFromSearchHistory = action.payload;
    },
    removeID_Image: (state, action: PayloadAction<string>) => {
      state.images = state.images.filter(img => img.imageId !== action.payload);
    },
    setID_ImageTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        tags: { tagName: string; tagId: string }[];
      }>,
    ) => {
      const targetImage = state.imageTags.find(tag => tag.imageId === action.payload.imageId);

      if (targetImage) {
        targetImage.tags = action.payload.tags;
      } else {
        state.imageTags.push(action.payload);
      }
    },
    setID_FeatureTree: (state, action: PayloadAction<InfringementDetectionFeatureTreeType | null>) => {
      state.featureTree = action.payload;
    },
    setCurrentSubsystemId: (state, action: PayloadAction<string>) => {
      state.currentSubsystemId = action.payload;
    },
    setID_FeatureTreeLoading: (state, action: PayloadAction<boolean>) => {
      state.featureTreeLoading = action.payload;
    },
    setIsLoadingScore: (state, action: PayloadAction<boolean>) => {
      state.isLoadingScore = action.payload;
    },
    setID_ImageAreaTags: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.imageAreaTags.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    setID_ImageAreaTagsTemp: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        const area = targetImage.areas.find(area => area.id === action.payload.area.id);
        if (area) {
          area.tags = action.payload.area.tags;
        } else {
          targetImage.areas.push(action.payload.area);
        }
      } else {
        state.imageAreaTagsTemp.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    setSubsytenAnalysis: (state, action: PayloadAction<{ subsystemAnalysisId: string; name: string }[]>) => {
      state.subsystemAnalysisInfo = action.payload;
    },
    addSubsystemAnalysis: (state, action: PayloadAction<{ subsystemAnalysisId: string; name: string }>) => {
      state.subsystemAnalysisInfo = [...state.subsystemAnalysisInfo, action.payload];
    },
    clearImageAreaTagsTemp: state => {
      state.imageAreaTagsTemp = [];
    },
    createArea: (
      state,
      action: PayloadAction<{
        imageId: string;
        area: AreaTagI;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas.push(action.payload.area);
      } else {
        state.imageAreaTagsTemp.push({
          imageId: action.payload.imageId,
          areas: [action.payload.area],
        });
      }
    },
    changeAreaColor: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
        color: string;
        temp: boolean;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      const targetImageTemp = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);

      if (action.payload.temp) {
        if (targetImageTemp) {
          const area = targetImageTemp.areas.find(area => area.id === action.payload.areaId);
          if (area) {
            area.color = action.payload.color;
          }
        }
      } else {
        if (targetImage) {
          const area = targetImage.areas.find(area => area.id === action.payload.areaId);
          if (area) {
            area.color = action.payload.color;
          }
        }
      }
    },
    deleteAreaFromImage: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
      }>,
    ) => {
      const targetImage = state.imageAreaTags.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas = targetImage.areas.filter(area => area.id !== action.payload.areaId);
      }
    },
    deleteTempAreaFromImage: (
      state,
      action: PayloadAction<{
        imageId: string;
        areaId: string;
      }>,
    ) => {
      const targetImage = state.imageAreaTagsTemp.find(tag => tag.imageId === action.payload.imageId);
      if (targetImage) {
        targetImage.areas = targetImage.areas.filter(area => area.id !== action.payload.areaId);
      }
    },
    setID_CountryCodes: (state, action: PayloadAction<string[]>) => {
      state.countryCodes = action.payload;
    },
    setID_Statuses: (state, action: PayloadAction<string[]>) => {
      state.statuses = action.payload;
    },
    setID_Assignees: (state, action: PayloadAction<string[]>) => {
      state.assignees = action.payload;
    },
    setID_PriorityFromDate: (state, action: PayloadAction<string>) => {
      state.priorityFromDate = action.payload;
    },
    setID_PriorityToDate: (state, action: PayloadAction<string>) => {
      state.priorityToDate = action.payload;
    },
    setID_PatentWithFeedback: (state, action: PayloadAction<string>) => {
      state.patentsWithFeedback.push(action.payload);
    },
    setID_UpdatedScores: (state, action: PayloadAction<UpdatedScoresType>) => {
      state.updatedScores = action.payload;
    },
    setID_UpdatedAt: (state, action: PayloadAction<string>) => {
      state.updatedAt = action.payload;
    },
    setID_Duplicate: (state, action: PayloadAction<InfringementDetectionI | null>) => {
      if (action.payload) {
        state.isDuplicateExist = true;
        state.id = action.payload.infringementDetectionId;
        state.searchName = action.payload.name;
        state.productDescription = action.payload.productDescription;
        state.images = action.payload.images.map(i => ({
          imageId: i.imageId,
          imageUrl: i.imageUrl,
        }));
        state.imageTags = action.payload.images.map(i => ({
          imageId: i.imageId,
          tags: i.imageTags.map(t => {
            const id = _uniqueId('tag-to-add-');

            return { tagName: t.tag, tagId: id };
          }),
        }));
        state.imageAreaTags = action.payload.images.map(i => ({
          imageId: i.imageId,
          areas: i.imageAreaTags.map(area => {
            return {
              id: area.imageTagAreaId,
              tags: area.tags.map(tag => ({ id: tag.tagId, name: tag.tag })),
              x: area.tagArea.xCoordinate,
              y: area.tagArea.yCoordinate,
              width: area.tagArea.width,
              height: area.tagArea.height,
              color: area.colour,
            };
          }),
        }));
        state.groupByFamily = action.payload.filters.groupByFamily;
        state.countryCodes = action.payload.filters.termFilters.find(f => f.field === 'COUNTRY_CODE')?.values ?? [];
        state.statuses = action.payload.filters.termFilters.find(f => f.field === 'PATENT_STATUS')?.values ?? [];
        state.priorityFromDate =
          action.payload.filters.dateRangeFilters.find(f => f.filterField === 'PRIORITY_DATE')?.fromDate ?? '';
        state.priorityToDate =
          action.payload.filters.dateRangeFilters.find(f => f.filterField === 'PRIORITY_DATE')?.toDate ?? '';
      } else {
        state.isDuplicateExist = false;
      }
    },
    regenerateFeatureTree: (state, action: PayloadAction<boolean>) => {
      state.regenerateTree = action.payload;
    },
    confirmTreeChanges: (state, action: PayloadAction<boolean>) => {
      state.saveTreeChanges = action.payload;
    },
    setSelectPatentsMode: (state, action: PayloadAction<boolean>) => {
      state.selectPatentsMode = action.payload;
    },
    setPatentsToGenerateClaims: (state, action: PayloadAction<string[]>) => {
      state.patentsToGenerateClaims = action.payload;
    },
    setNewPatentsToGenerateClaims: (state, action: PayloadAction<string[]>) => {
      state.newPatentsToGenerateClaims = action.payload;
    },
    setGenerateClaimsMode: (state, action: PayloadAction<boolean>) => {
      state.generateClaimsMode = action.payload;
    },
    deleteAnalysis: (state, action: PayloadAction<string>) => {
      const analysisList = state.subsystemAnalysisInfo.filter(
        element => element.subsystemAnalysisId !== action.payload,
      );

      state.subsystemAnalysisInfo = [...analysisList];
    },
    setSelectedClaim: (
      state,
      action: PayloadAction<{
        claimId: string;
        patentId: string;
        sectionId: string;
        sectionPosition: number;
      } | null>,
    ) => {
      state.selectedClaim = action.payload;
    },
    resetInfringementDetectionFilters: state => ({
      ...state,
      countryCodes: initialState.countryCodes,
      statuses: initialState.statuses,
      priorityFromDate: initialState.priorityFromDate,
      priorityToDate: initialState.priorityToDate,
    }),

    resetInfringementDetectionSlice: () => initialState,
  },
});

export default infringementDetectionSlice.reducer;

// Action creators are generated for each case reducer function
export const {
  setID_SearchName,
  setID_Id,
  setID_ProductDescription,
  setID_Images,
  removeID_Image,
  setID_ImageTags,
  setID_ImageAreaTags,
  setID_ImageAreaTagsTemp,
  changeAreaColor,
  setID_ImagesFromSearchHistory,
  setID_FeatureTree,
  setID_FeatureTreeLoading,
  setID_GroupByFamily,
  setID_CountryCodes,
  setID_Statuses,
  setID_Assignees,
  setID_PriorityFromDate,
  setID_PriorityToDate,
  setID_PatentWithFeedback,
  setID_UpdatedScores,
  setID_UpdatedAt,
  setID_Duplicate,
  setSubsytenAnalysis,
  addSubsystemAnalysis,
  regenerateFeatureTree,
  resetInfringementDetectionSlice,
  createArea,
  deleteAreaFromImage,
  deleteTempAreaFromImage,

  clearImageAreaTagsTemp,
  setGenerateClaimsMode,
  setSelectPatentsMode,
  setPatentsToGenerateClaims,
  setNewPatentsToGenerateClaims,
  setSelectedClaim,
  setID_PDF,
  removeID_PDF,
  setID_WordFile,
  removeID_WordFile,
  deleteAnalysis,
  setCurrentSubsystemId,
  setIsLoadingScore,
  resetInfringementDetectionFilters,
} = infringementDetectionSlice.actions;
